import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { TipoRdt } from '@/types/api';

/** Tamaño de la PLACA neutra de la Definitiva (radio y padding; el tamaño de
 *  letra lo sigue pasando el caller por `className`):
 *  - 'base' (default): la de siempre, pensada para ~14 px (card de la obra, ficha).
 *  - 'card': el `#nro` de 23 px de la card del mosaico del Avivador v2 (radio 7,
 *    padding 0 8 px: D-4 del relevamiento de la card, igual que el artifact).
 *  - 'panel': el de 26 px del panel lateral del Avivador v2 (el artifact usa
 *    la misma placa que la card). */
type TamanoPlaca = 'base' | 'card' | 'panel';

const PLACA_CLASS: Record<TamanoPlaca, string> = {
  base: 'rounded-[6px] px-[7px]',
  card: 'rounded-[7px] px-2',
  panel: 'rounded-[7px] px-2',
};

interface Props {
  nro: string;
  tipo: TipoRdt;
  className?: string;
  /** Versión compacta (⌘K de global-search.tsx): radio y padding más chicos
   *  que la placa "de card" default, para no pesar en una fila de resultado.
   *  Si está, manda sobre `tamano`. */
  compact?: boolean;
  /** Sin el `title="Definitiva"` nativo (Avivador v2: nunca `title` en el
   *  mosaico, y la palabra ya está al lado). Default false: como siempre. */
  sinTitle?: boolean;
  /** Radio y padding de la placa de la Definitiva. Default 'base': como siempre. */
  tamano?: TamanoPlaca;
}

// Distinción visual Provisoria/Definitiva en el número — decisión de Juan
// (QA en vivo, 2026-09-14) tras un preview con 8 tratamientos y el panel
// "monos sabios": el número de la Definitiva va sobre la PLACA NEUTRA
// (token bg-plate, el mismo que usa la placa del CC sin color de cliente en
// header-obra.tsx / obra-card.tsx), con texto normal (text-foreground) SIN
// invertir — se probó el tratamiento invertido (fondo sólido + texto claro)
// y se descartó ("negro es mucho"). Las Provisorias no llevan placa: quedan
// tal cual el caller las pase. La PALABRA "Definitiva"/"Provisoria" al lado
// se mantiene exactamente como está hoy (pedido explícito de Juan) — este
// componente NO la toca, solo envuelve el número. Nada de color acá: el
// estado sigue siendo el único eje de color de la card (paleta B).
export function NroRdt({ nro, tipo, className, compact = false, sinTitle = false, tamano = 'base' }: Props) {
  const esDefinitiva = tipo === 'Definitiva';
  const clase = cn(
    className,
    // text-foreground va DESPUÉS de `className` a propósito: mismo
    // patrón que obra-card.tsx / header-obra.tsx (bg-plate siempre
    // empareja con text-foreground) — pisa un color heredado del caller
    // (ej. text-text-tertiary en la fila compacta del ⌘K) para que el
    // texto quede "normal" sobre la placa como pidió Juan, no atenuado.
    esDefinitiva && cn('bg-plate text-foreground', compact ? 'rounded-[4px] px-[5px]' : PLACA_CLASS[tamano]),
    // Dark: la placa (#34343c) sobre la card (#232327) contrasta 1,27:1 y
    // casi no se ve (lente de accesibilidad, 2026-09-14); un anillo
    // interior con border-strong la recorta sin invertir nada. En light
    // la placa se lee sola (el anillo no aplica).
    esDefinitiva && 'dark:ring-1 dark:ring-inset dark:ring-border-strong',
  );

  // R3-C (Ajuste 13): sin `title` nativo. La palabra "Definitiva" ya está al
  // lado en todos los usos de hoy (comentario de arriba) salvo un par que no
  // la muestran (ej. auditoria.tsx) — se mantiene el Tooltip de ui/ (no un
  // <span> plano) para no perder esa ayuda ahí, con la misma condición y el
  // mismo `sinTitle` de siempre para el mosaico.
  if (esDefinitiva && !sinTitle) {
    return (
      <Tooltip>
        <TooltipTrigger render={<span className={clase} />}>#{nro}</TooltipTrigger>
        <TooltipContent>Definitiva</TooltipContent>
      </Tooltip>
    );
  }

  return <span className={clase}>#{nro}</span>;
}
